/*
  Delune virtual try-on: lipstick, lip liner and brow shades on the shopper's own face, live from their camera or
  on a photo they choose. Markup: snippets/delune-try-on.liquid. Styles: assets/delune-try-on.css.

  Face tracking is Google's MediaPipe Face Landmarker. It is fetched only when the shopper starts (nothing on page
  load) and runs in the browser: frames go from the camera to a canvas on this page and nowhere else.
*/
(function () {
  'use strict';
  if (customElements.get('delune-try-on')) return;

  var VISION = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21';
  var MODEL =
    'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

  // Face Landmarker point numbers: the outer and inner edge of the lips, and each brow (top edge out, bottom edge back)
  var LIPS_OUTER = [61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37, 39, 40, 185];
  var LIPS_INNER = [78, 95, 88, 178, 87, 14, 317, 402, 318, 324, 308, 415, 310, 311, 312, 13, 82, 81, 80, 191];
  var BROWS = [
    [70, 63, 105, 66, 107, 55, 65, 52, 53, 46],
    [300, 293, 334, 296, 336, 285, 295, 282, 283, 276],
  ];

  var distance = function (a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  // A closed curve through the midpoints between the points, so an edge is round rather than faceted
  var ring = function (context, points) {
    var count = points.length;
    var between = function (a, b) {
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };
    var start = between(points[count - 1], points[0]);
    context.moveTo(start.x, start.y);
    for (var i = 0; i < count; i += 1) {
      var next = between(points[i], points[(i + 1) % count]);
      context.quadraticCurveTo(points[i].x, points[i].y, next.x, next.y);
    }
    context.closePath();
  };

  // Soft-edged colour that darkens what is under it, the way pigment does: the shape is drawn off to the side and
  // only its blurred shadow lands on the picture (canvas blur filters are not available in every browser)
  var soft = function (context, colour, strength, blur, draw) {
    var aside = context.canvas.width * 2;
    context.save();
    context.globalCompositeOperation = 'multiply';
    context.globalAlpha = strength;
    context.shadowColor = colour;
    context.shadowBlur = blur;
    context.shadowOffsetX = aside;
    context.fillStyle = colour;
    context.strokeStyle = colour;
    context.translate(-aside, 0);
    context.beginPath();
    draw();
    context.restore();
  };

  // How strongly each product shows with the Lighter/Heavier slider at its heaviest
  var FULL = { lips: 0.9, liner: 0.95, brows: 0.7 };

  // look: { lips, liner, brows }, each a colour or null. weight: the same keys, each the slider's 0.2 to 1.
  // at(n) gives point n of the face in canvas pixels.
  var paint = function (context, at, look, weight) {
    var lipWidth = distance(at(61), at(291));
    if (look.brows) {
      BROWS.forEach(function (brow) {
        var points = brow.map(at);
        soft(context, look.brows, FULL.brows * weight.brows, distance(points[0], points[4]) * 0.08, function () {
          ring(context, points);
          context.fill();
        });
      });
    }
    if (look.lips) {
      soft(context, look.lips, FULL.lips * weight.lips, lipWidth * 0.03, function () {
        ring(context, LIPS_OUTER.map(at));
        ring(context, LIPS_INNER.map(at));
        context.fill('evenodd');
      });
    }
    if (look.liner) {
      soft(context, look.liner, FULL.liner * weight.liner, lipWidth * 0.015, function () {
        context.lineWidth = Math.max(1.5, lipWidth * 0.028);
        context.lineJoin = 'round';
        ring(context, LIPS_OUTER.map(at));
        context.stroke();
      });
    }
  };

  class DeluneTryOn extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.dialog = this.querySelector('dialog');
      this.canvas = this.querySelector('canvas');
      this.context = this.canvas.getContext('2d');
      this.video = this.querySelector('video');
      this.hint = this.querySelector('[data-dto-hint]');
      this.setList = this.querySelector('[data-dto-set]');
      this.choice = this.querySelector('[data-dto-choice]');
      this.addButton = this.querySelector('[data-dto-add]');
      this.seeLink = this.querySelector('[data-dto-see]');
      this.weightSlider = this.querySelector('[data-dto-weight]');
      this.look = { lips: null, liner: null, brows: null };
      this.weight = { lips: 0.6, liner: 0.6, brows: 0.6 };
      this.picked = {};
      this.mode = null;
      this.loop = this.loop.bind(this);

      this.addEventListener('click', (event) => {
        var target = event.target.closest(
          '[data-dto-start],[data-dto-close],[data-dto-tab],[data-dto-shade],[data-dto-add],[data-dto-clear]'
        );
        if (!target) return;
        if (target.hasAttribute('data-dto-start')) this.startCamera();
        else if (target.hasAttribute('data-dto-clear')) this.clear(target.dataset.dtoClear);
        else if (target.hasAttribute('data-dto-close')) this.dialog.close();
        else if (target.hasAttribute('data-dto-tab')) this.showTab(target.dataset.dtoTab);
        else if (target.hasAttribute('data-dto-shade')) this.pick(target);
        else if (target.hasAttribute('data-dto-add')) this.addToBag();
      });
      // Lighter to heavier: kept for each product, so a sheer lipstick can sit with a firm liner
      this.weightSlider.addEventListener('input', () => {
        this.weight[this.mode] = Number(this.weightSlider.value) / 100;
        if (this.photo) this.draw(this.photo.source, this.photo.face);
      });
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-dto-photo]')) return;
        if (event.target.files && event.target.files[0]) this.usePhoto(event.target.files[0]);
        event.target.value = '';
      });

      var compare = this.querySelector('[data-dto-compare]');
      var comparing = (on) => {
        this.comparing = on;
        if (this.photo) this.draw(this.photo.source, this.photo.face);
      };
      compare.addEventListener('pointerdown', () => comparing(true));
      ['pointerup', 'pointerleave', 'pointercancel'].forEach((name) => compare.addEventListener(name, () => comparing(false)));
      compare.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter') comparing(true);
      });
      compare.addEventListener('keyup', () => comparing(false));

      this.dialog.addEventListener('close', () => this.closed());

      // Anything on the page marked data-try-on-open opens it, on the tab named in data-try-on-mode if there is one
      document.addEventListener('click', (event) => {
        var opener = event.target.closest('[data-try-on-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener.dataset.tryOnMode);
      });
    }

    setState(state) {
      this.dataset.state = state;
    }

    open(mode) {
      if (this.dialog.open) return;
      var first = this.querySelector('[data-dto-tab]');
      var wanted = (mode && this.querySelector('[data-dto-tab="' + mode + '"]')) || first;
      if (!wanted) return;
      this.added = null;
      this.setState('consent');
      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      this.showTab(wanted.dataset.dtoTab);
      // Something to see the moment the camera starts: the first shade of the tab it opened on
      if (!this.picked[this.mode]) {
        var shade = this.querySelector('[data-dto-shades="' + this.mode + '"] [data-dto-shade][data-colour]');
        if (shade) this.pick(shade);
      }
    }

    closed() {
      this.stopCamera();
      this.photo = null;
      document.body.classList.remove('overflow-hidden');
      // Shades added while trying on: show them in the bag now
      var cart = document.querySelector('cart-drawer');
      if (this.added && cart && cart.renderContents) cart.renderContents(this.added);
      this.added = null;
    }

    showTab(mode) {
      this.mode = mode;
      this.querySelectorAll('[data-dto-tab]').forEach((tab) => {
        tab.setAttribute('aria-selected', tab.dataset.dtoTab === mode ? 'true' : 'false');
      });
      this.querySelectorAll('[data-dto-shades]').forEach((panel) => {
        panel.hidden = panel.dataset.dtoShades !== mode;
      });
      this.weightSlider.value = Math.round(this.weight[mode] * 100);
      this.describe();
    }

    pick(shade) {
      var mode = shade.dataset.mode;
      shade.parentElement.querySelectorAll('[data-dto-shade]').forEach((other) => {
        other.setAttribute('aria-pressed', other === shade ? 'true' : 'false');
      });
      this.look[mode] = shade.dataset.colour || null;
      this.picked[mode] = shade.dataset.colour ? shade : null;
      this.describe();
      this.listSet();
      if (this.photo) this.draw(this.photo.source, this.photo.face);
    }

    // Take one product off again (the same as choosing "none" on its tab)
    clear(mode) {
      var none = this.querySelector('[data-dto-shades="' + mode + '"] .dto__shade--none');
      if (none) this.pick(none);
    }

    // What is on, as a short list over the picture: "Lipstick: Jet-Set Red ×". Each line takes its product off.
    listSet() {
      this.setList.textContent = '';
      ['lips', 'liner', 'brows'].forEach((mode) => {
        var shade = this.picked[mode];
        var tab = this.querySelector('[data-dto-tab="' + mode + '"]');
        if (!shade || !tab) return;
        var product = tab.textContent.trim();
        var line = document.createElement('button');
        line.type = 'button';
        line.className = 'dto__chip';
        line.dataset.dtoClear = mode;
        line.setAttribute('aria-label', (this.dataset.removeLabel || 'Remove %s').replace('%s', product.toLowerCase()) + ': ' + shade.dataset.name);
        var dot = document.createElement('i');
        dot.style.background = shade.dataset.colour;
        var words = document.createElement('span');
        words.textContent = product + ': ' + shade.dataset.name;
        var cross = document.createElement('b');
        cross.textContent = '\u00d7';
        cross.setAttribute('aria-hidden', 'true');
        line.append(dot, words, cross);
        this.setList.appendChild(line);
      });
    }

    // The line above Add to bag: the shade chosen on the tab showing, its price, and where "See product" goes
    describe() {
      var panel = this.querySelector('[data-dto-shades="' + this.mode + '"]');
      var shade = this.picked[this.mode];
      if (!panel) return;
      this.seeLink.href = panel.dataset.url;
      this.choice.textContent = '';
      var name = document.createElement('span');
      name.textContent = shade ? shade.dataset.name : panel.dataset.title;
      this.choice.appendChild(name);
      if (shade) {
        var price = document.createElement('strong');
        price.textContent = shade.dataset.price;
        this.choice.appendChild(price);
      }
      var soldOut = shade && !shade.hasAttribute('data-available');
      this.addButton.disabled = !shade || soldOut;
      this.addButton.textContent = soldOut ? this.addButton.dataset.soldOut : this.addButton.dataset.label;
    }

    load() {
      if (!this.ready) {
        this.ready = import(VISION + '/vision_bundle.mjs').then((vision) =>
          vision.FilesetResolver.forVisionTasks(VISION + '/wasm').then((files) => {
            var create = (delegate) =>
              vision.FaceLandmarker.createFromOptions(files, {
                baseOptions: { modelAssetPath: MODEL, delegate: delegate },
                runningMode: 'VIDEO',
                numFaces: 1,
              });
            return create('GPU').catch(() => create('CPU'));
          })
        );
        this.ready.catch(() => {
          this.ready = null;
        });
      }
      return this.ready;
    }

    startCamera() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return this.setState('denied');
      this.setState('loading');
      this.photo = null;
      var camera = navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      camera.catch(() => this.setState('denied'));
      Promise.all([this.load(), camera])
        .then((results) => {
          this.landmarker = results[0];
          this.stream = results[1];
          if (!this.dialog.open) return this.stopCamera();
          this.video.srcObject = this.stream;
          return Promise.all([this.landmarker.setOptions({ runningMode: 'VIDEO' }), this.video.play()]).then(() => {
            this.smooth = null;
            this.dataset.source = 'camera';
            this.setState('live');
            this.running = true;
            this.loop();
          });
        })
        .catch(() => {
          this.stopCamera();
          if (this.dataset.state !== 'denied') this.setState('error');
        });
    }

    stopCamera() {
      this.running = false;
      cancelAnimationFrame(this.frame);
      if (this.stream) this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
      this.video.srcObject = null;
    }

    loop() {
      if (!this.running) return;
      var video = this.video;
      if (video.readyState >= 2 && video.videoWidth) {
        var result = this.landmarker.detectForVideo(video, performance.now());
        this.draw(video, result.faceLandmarks && result.faceLandmarks[0], true);
      }
      this.frame = requestAnimationFrame(this.loop);
    }

    // source: the video, or a picture (an image, bitmap or canvas). A File is read first.
    usePhoto(source) {
      this.stopCamera();
      this.setState('loading');
      var picture =
        source instanceof Blob
          ? createImageBitmap(source, { imageOrientation: 'from-image' }).catch(() => createImageBitmap(source))
          : Promise.resolve(source);
      return Promise.all([this.load(), picture])
        .then((results) => {
          this.landmarker = results[0];
          // Large photos are brought down to a size that tracks quickly and still looks sharp
          var image = results[1];
          var width = image.naturalWidth || image.videoWidth || image.width;
          var height = image.naturalHeight || image.videoHeight || image.height;
          var scale = Math.min(1, 1600 / Math.max(width, height));
          var still = document.createElement('canvas');
          still.width = Math.round(width * scale);
          still.height = Math.round(height * scale);
          still.getContext('2d').drawImage(image, 0, 0, still.width, still.height);
          return this.landmarker.setOptions({ runningMode: 'IMAGE' }).then(() => {
            var result = this.landmarker.detect(still);
            this.photo = { source: still, face: result.faceLandmarks && result.faceLandmarks[0] };
            this.dataset.source = 'photo';
            this.setState('live');
            this.draw(still, this.photo.face);
          });
        })
        .catch(() => this.setState('error'));
    }

    draw(source, face, moving) {
      var canvas = this.canvas;
      var context = this.context;
      var width = source.videoWidth || source.width;
      var height = source.videoHeight || source.height;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      context.globalCompositeOperation = 'source-over';
      context.globalAlpha = 1;
      context.drawImage(source, 0, 0, width, height);
      this.hint.hidden = !!face;
      if (!face) {
        this.smooth = null;
        return;
      }
      if (this.comparing) return;
      // Live: each point eases towards where the tracker now sees it, which takes the shimmer out of the edges
      var points = face;
      if (moving) {
        if (!this.smooth || this.smooth.length !== face.length) {
          this.smooth = face.map((point) => ({ x: point.x, y: point.y }));
        } else {
          for (var i = 0; i < face.length; i += 1) {
            this.smooth[i].x += (face[i].x - this.smooth[i].x) * 0.6;
            this.smooth[i].y += (face[i].y - this.smooth[i].y) * 0.6;
          }
        }
        points = this.smooth;
      }
      paint(
        context,
        function (index) {
          return { x: points[index].x * width, y: points[index].y * height };
        },
        this.look,
        this.weight
      );
    }

    addToBag() {
      var shade = this.picked[this.mode];
      if (!shade || this.addButton.disabled) return;
      var cart = document.querySelector('cart-drawer');
      var body = { id: Number(shade.dataset.variant), quantity: 1 };
      if (cart && cart.getSectionsToRender) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      this.addButton.disabled = true;
      fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.status) throw new Error(state.description || state.message);
          this.added = state;
          this.addButton.textContent = this.addButton.dataset.added;
        })
        .catch(() => {
          this.addButton.textContent = this.addButton.dataset.failed;
        })
        .then(() => {
          setTimeout(() => this.describe(), 1800);
        });
    }
  }

  customElements.define('delune-try-on', DeluneTryOn);
})();
